/**
 * HUNA.PK — Homepage Coordinated Premium Motion Engine
 * Online Store 2.0 progressive enhancement.
 *
 * Guarantees:
 * - Content is 100% visible before, during, and without JavaScript.
 * - Zero hidden pending states or reliance on callbacks/watchdogs for visibility.
 * - Finite Web Animations API execution with fill: 'none' (reverts cleanly to CSS).
 * - Initial viewport and focused elements are skipped entirely.
 * - Idempotent init, disable, enable, and Shopify Theme Editor lifecycle support.
 */
(() => {
  'use strict';
  if (window.HunaMotion) return;

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 767px)');

  // Targeted below-fold editorial text and media (never parent + child together)
  const revealSelector = [
    '.huna-lenses .huna-lenses__intro',
    '.huna-lenses .huna-lens__media',
    '.huna-uc .huna-uc__media',
    '.huna-compare .huna-compare__head',
    '.huna-sb .huna-sb__media',
    '.huna-sb .huna-sb__copy > .huna-h-lg',
    '.huna-story .huna-story__q',
    '#reviews .huna-cm__head',
    '#reviews .huna-cm__review-card'
  ].join(',');

  const seen = new WeakSet();
  const targets = new Set();
  const surfaces = new Set();
  const animations = new Map();
  const pending = new Set();
  let observer = null;
  let disabled = root.getAttribute('data-huna-motion') === 'disabled';
  let destroyed = false;

  function cancel(element) {
    const animation = animations.get(element);
    if (!animation) return;
    animations.delete(element);
    try {
      animation.cancel();
    } catch (_) {}
  }

  function disconnect() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    pending.clear();
    animations.forEach((animation) => {
      try {
        animation.cancel();
      } catch (_) {}
    });
    animations.clear();
    targets.forEach((element) => element.removeAttribute('data-huna-motion-reveal'));
    surfaces.forEach((element) => element.removeAttribute('data-huna-motion-surface'));
    targets.clear();
    surfaces.clear();
  }

  function finishScope(scope) {
    targets.forEach((element) => {
      if (scope !== element && !scope.contains(element)) return;
      seen.add(element);
      pending.delete(element);
      if (observer) observer.unobserve(element);
      cancel(element);
    });
    if (observer && !pending.size) {
      observer.disconnect();
      observer = null;
    }
  }

  function reveal(element, order) {
    seen.add(element);
    if (!element.isConnected || element.contains(document.activeElement)) return;

    // Content is already fully visible in CSS.
    // Web Animations API applies a temporary finite animation with fill: 'none'.
    // If interrupted or when finished, the element remains in its natural visible CSS state.
    try {
      const isMobile = mobile.matches;
      // Movement: Desktop 6-8px (7px), Mobile 4-6px (5px)
      const distance = isMobile ? 5 : 7;
      // Duration: Desktop 320-380ms (340ms), Mobile 220-260ms (240ms)
      const duration = isMobile ? 240 : 340;
      // Stagger: 35-45ms, capped so a group finishes within 500ms
      const stagger = isMobile ? 35 : 40;
      const delay = Math.min((order % 4) * stagger, isMobile ? 140 : 160);

      const animation = element.animate([
        { opacity: 0.8, transform: `translate3d(0, ${distance}px, 0)` },
        { opacity: 1, transform: 'translate3d(0, 0, 0)' }
      ], {
        duration,
        delay,
        easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
        fill: 'none'
      });

      animations.set(element, animation);
      const release = () => {
        if (animations.get(element) === animation) animations.delete(element);
      };
      animation.finished.then(release, release);
    } catch (_) {
      // Graceful fallback: element is already 100% visible in CSS
    }
  }

  function init() {
    if (destroyed) return;
    disconnect();

    const isReduced = reduced.matches;
    root.setAttribute('data-huna-motion', disabled ? 'disabled' : isReduced ? 'reduced' : 'enabled');

    if (disabled || isReduced || !('IntersectionObserver' in window) ||
        typeof Element.prototype.animate !== 'function') {
      return;
    }

    const main = document.getElementById('MainContent');
    if (!main) return;

    // Register interactive hover surfaces (lens cards & use cases)
    main.querySelectorAll('.huna-lenses .huna-lens, .huna-uc .huna-uc__tile').forEach((element) => {
      element.setAttribute('data-huna-motion-surface', element.classList.contains('huna-lens') ? 'lens' : 'use-case');
      surfaces.add(element);
    });

    const vh = window.innerHeight || document.documentElement.clientHeight;

    // Register below-fold reveal targets
    main.querySelectorAll(revealSelector).forEach((element) => {
      if (element.closest('[hidden], [aria-hidden="true"]')) return;
      targets.add(element);
      element.setAttribute('data-huna-motion-reveal', '');

      // Strictly skip elements already in the initial viewport, restored scroll position, or focused
      const rect = element.getBoundingClientRect();
      if (rect.top < vh || element.contains(document.activeElement)) {
        seen.add(element);
      }

      if (!seen.has(element)) {
        pending.add(element);
      }
    });

    if (!pending.size) return;

    try {
      observer = new IntersectionObserver((entries) => {
        let order = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !pending.has(entry.target)) return;
          pending.delete(entry.target);
          if (observer) observer.unobserve(entry.target);
          if (!disabled && !reduced.matches) {
            reveal(entry.target, order++);
          }
        });
        if (!pending.size && observer) {
          observer.disconnect();
          observer = null;
        }
      }, { threshold: 0.12, rootMargin: '0px 0px -24px 0px' });

      pending.forEach((element) => observer.observe(element));
    } catch (_) {
      disconnect();
    }
  }

  const onLoad = () => init();
  const onUnload = (event) => {
    const scope = event.target;
    if (!(scope instanceof Element)) return;
    finishScope(scope);
    [...targets].forEach((element) => {
      if (scope === element || scope.contains(element)) {
        element.removeAttribute('data-huna-motion-reveal');
        targets.delete(element);
      }
    });
    [...surfaces].forEach((element) => {
      if (scope === element || scope.contains(element)) {
        element.removeAttribute('data-huna-motion-surface');
        surfaces.delete(element);
      }
    });
  };
  const onSelect = (event) => {
    if (event.target instanceof Element) finishScope(event.target);
  };
  const onFocus = (event) => {
    targets.forEach((element) => {
      if (event.target instanceof Element && element.contains(event.target)) finishScope(element);
    });
  };
  const onPageShow = (event) => {
    if (event.persisted) init();
  };

  const editorEvents = [
    ['shopify:section:load', onLoad],
    ['shopify:section:unload', onUnload],
    ['shopify:section:select', onSelect],
    ['shopify:block:select', onSelect],
    ['focusin', onFocus]
  ];

  window.HunaMotion = Object.freeze({
    init,
    disable() {
      disabled = true;
      root.setAttribute('data-huna-motion', 'disabled');
      disconnect();
    },
    enable() {
      disabled = false;
      root.setAttribute('data-huna-motion', 'enabled');
      init();
    },
    destroy() {
      destroyed = true;
      disconnect();
      root.setAttribute('data-huna-motion', 'disabled');
      editorEvents.forEach(([name, listener]) => document.removeEventListener(name, listener));
      window.removeEventListener('pageshow', onPageShow);
      if (reduced.removeEventListener) reduced.removeEventListener('change', onLoad);
      else if (reduced.removeListener) reduced.removeListener(onLoad);
      document.removeEventListener('DOMContentLoaded', onLoad);
      delete window.HunaMotion;
    }
  });

  editorEvents.forEach(([name, listener]) => document.addEventListener(name, listener));
  window.addEventListener('pageshow', onPageShow);
  if (reduced.addEventListener) reduced.addEventListener('change', onLoad);
  else if (reduced.addListener) reduced.addListener(onLoad);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad, { once: true });
  } else {
    init();
  }
})();
